import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Plus, Pencil, Link2, X, BookMarked, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Active", "Paused", "Completed"];
const STATUS_COLOR = { Active: "bg-emerald-100 text-emerald-700", Paused: "bg-amber-100 text-amber-700", Completed: "bg-sky-100 text-sky-700" };
const empty = { title: "", description: "", category: "", status: "Active", links: [] };

export default function ResearchTopics() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [linkForm, setLinkForm] = useState({ label: "", url: "" });

  const load = () => api.get("/topics").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setLinkForm({ label: "", url: "" }); setOpen(true); };
  const openEdit = (t) => { setForm({ ...empty, ...t }); setEditId(t.id); setLinkForm({ label: "", url: "" }); setOpen(true); };
  const addLink = () => {
    if (!linkForm.url.trim()) return;
    setForm({ ...form, links: [...form.links, { id: crypto.randomUUID(), label: linkForm.label || linkForm.url, url: linkForm.url }] });
    setLinkForm({ label: "", url: "" });
  };
  const removeLink = (id) => setForm({ ...form, links: form.links.filter((l) => l.id !== id) });
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      if (editId) await api.put(`/topics/${editId}`, form);
      else await api.post("/topics", form);
      setOpen(false); load(); toast.success(editId ? "Topic updated" : "Topic added");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/topics/${id}`, { params: { permanent: true } }); load(); toast.success("Topic deleted"); };

  return (
    <div className="space-y-6" data-testid="research-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Research Topics</h1>
          <p className="text-muted-foreground">Track topics you're researching and keep all reference links in one place.</p>
        </div>
        <Button onClick={openNew} data-testid="new-topic-btn"><Plus size={16} className="mr-1" /> New Topic</Button>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {items.length === 0 && <p className="text-muted-foreground">No research topics yet. Add your first one.</p>}
        {items.map((t) => (
          <Card key={t.id} className="flex flex-col p-5 border-slate-200 shadow-sm transition-transform hover:-translate-y-0.5" data-testid={`topic-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2"><BookMarked size={16} className="text-primary" /><h3 className="font-head font-bold text-slate-900">{t.title}</h3></div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(t)} className="text-slate-400 hover:text-primary" data-testid={`edit-topic-${t.id}`}><Pencil size={15} /></button>
                <ConfirmDelete onConfirm={() => del(t.id)} name={t.title} testid={`delete-topic-${t.id}`} />
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge className={STATUS_COLOR[t.status]}>{t.status}</Badge>
              {t.category && <Badge variant="outline">{t.category}</Badge>}
            </div>
            {t.description && <p className="mt-2 text-sm text-slate-600">{t.description}</p>}
            {t.links?.length > 0 && (
              <div className="mt-3 space-y-1 border-t border-slate-100 pt-3">
                <p className="text-[11px] font-medium text-muted-foreground">Links · {t.links.length}</p>
                {t.links.map((l) => (
                  <a key={l.id} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-secondary hover:underline" data-testid={`topic-link-${l.id}`}>
                    <Link2 size={12} /> <span className="truncate">{l.label}</span> <ExternalLink size={11} />
                  </a>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="topic-modal">
          <DialogHeader><DialogTitle>{editId ? "Edit Topic" : "New Research Topic"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Exosome therapy landscape" data-testid="topic-title-input" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="topic-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Biotech, Devices..." data-testid="topic-category-input" /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="topic-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div>
              <Label>Reference Links</Label>
              <div className="mt-1 grid grid-cols-[1fr_1.4fr_auto] gap-2">
                <Input value={linkForm.label} onChange={(e) => setLinkForm({ ...linkForm, label: e.target.value })} placeholder="Label" data-testid="topic-link-label" />
                <Input value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLink())} placeholder="https://..." data-testid="topic-link-url" />
                <Button type="button" variant="secondary" onClick={addLink} data-testid="topic-add-link-btn"><Plus size={15} /></Button>
              </div>
              <ul className="mt-2 space-y-1">
                {form.links.map((l) => (
                  <li key={l.id} className="flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5 text-sm">
                    <Link2 size={13} className="text-secondary" />
                    <span className="flex-1 truncate">{l.label} — <span className="text-muted-foreground">{l.url}</span></span>
                    <button onClick={() => removeLink(l.id)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-topic-btn">{editId ? "Save Changes" : "Add Topic"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
